import React, { useEffect, useMemo, useState } from "react";
import {
  FormSections,
  ProgramContainer,
} from "../../entities/formTemplateDataType";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import axios from "axios";
import DataUtils, { sanitizeEmail } from "../../utilities/DataUtils";
import ReCaptchaV3 from "../captcha/CaptchComponent";
import ReCaptchaButton from "../captcha/captcha-button";
import { getCxpMessages } from "../../services/CxpProfileService";
import SectionUtils from "../../utilities/SectionUtils";
import { transformToPdfPayload } from "../../utilities/VeconFieldMappings";
import { VECON_PDF_FILES } from "../../entities/VeconConstants";
import { differenceInYears } from "date-fns";
import SessionStorageService from "../../services/SessionStorageService";
import { TOKEN_TYPE } from "../../entities/Constants";

interface Props {
  token: string;
  programContainer: ProgramContainer;
  isEng: boolean;
  section: FormSections;
  onButtonClick: (data: any) => void;
  callback?: (p: ProgramContainer) => void;
  callbackStepIndex?: (index: number, returnIndex?: number) => void;
}

const VECONHandleSubmitComponent: React.FC<Props> = ({
  programContainer,
  isEng,
  section,
  onButtonClick,
  callback,
  callbackStepIndex,
}) => {
  const [validationErrMsg, setValidationErrMsg] = useState<any[]>([]);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [confirmationVisible, setConfirmationVisible] =
    useState<boolean>(false);
  const [summaryVisible, setSummaryVisible] = useState<boolean>(true);
  const [transactionId, setTransactionId] = useState<string>("");
  const [submissionError, setSubmissionError] = useState<boolean>(false);
  const [renewalResponse, setRenewalResponse] = useState<any>(null);
  const [captchaToken, setCaptchaToken] = useState<string>("");

  const cleanupAfterSubmit = () => {
    if (!programContainer.ClearFormAfterSubmit) {
      return;
    }
    DataUtils.clearFormValue(programContainer);
    SectionUtils.resetDirtyFlags(programContainer);
  };

  const data = DataUtils.collectFormValue(programContainer);
  const pdfApiPayload = transformToPdfPayload(data, programContainer);

  const updateErrorResponse = (data: any, isOverrideMessage?: boolean) => {
    let newContainer = { ...programContainer } as ProgramContainer;
    const errorSection = newContainer?.ListOfFormSections?.find(
      (formSection) => formSection?.IsTechErrorPage,
    );
    if (errorSection) {
      errorSection.apiResponse = data;
      const errorElement = errorSection.ListOfFormSubSections.flatMap(
        (subSection) => subSection?.ListOfFormSectionElements || [],
      ).find((element) => element?.LoadBindName === "errorMessageContent");

      if (errorElement && isOverrideMessage) {
        errorElement.Value = data?.messageEn;
        errorElement.ValueFRA = data?.messageFr;
      }
    }

    callback(newContainer);
  };

  const shouldOverrideMessage = (response: any): boolean => {
    return (
      response?.status === 200 &&
      response?.data?.isHardstop &&
      response?.data?.errorCode?.length > 0
    );
  };

  const handleSubmit = async (recaptchaToken: string) => {
    setCaptchaToken(recaptchaToken);
    let invalidControlList = FormValidationUtils.invalidControls(section);
    const language = isEng ? "EN" : "FR";

    if (invalidControlList.length > 0) {
      setValidationErrMsg(invalidControlList);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(
        () => document.getElementById("error-container")?.focus(),
        100,
      );
    } else {
      const url = programContainer.APIBasePath + programContainer.UpdateAPI;

      const isMarriedOrConjugal =
        data?.isMarried === "true" ||
        data?.isConjugalRelationshipValid === "true";
      const applicantAge = differenceInYears(
        new Date(),
        new Date(
          Number(pdfApiPayload?.dobYear),
          Number(pdfApiPayload?.dobMonth) - 1,
          Number(pdfApiPayload?.dobDay),
        ),
      );
      const isMinor = applicantAge === 16 || applicantAge === 17;
      const numberOfConsentPersons = Number(data?.numberOfCustodyPersons) || 0;

      const isConsentFormIncluded = (fileName: string): boolean => {
        if (!fileName.startsWith("VECON_ConsentForm")) return true;
        if (!(isMinor && data?.isMarried !== "true")) return false;
        const match = fileName.match(/^VECON_ConsentForm(\d*)/);
        const formNumber = match?.[1] ? Number(match[1]) : 1;
        return formNumber <= numberOfConsentPersons;
      };

      const record = {
        ...pdfApiPayload,
      };

      const payload = {
        emailAddress: sanitizeEmail(data?.emailAddress) || "",
        saveCopyConsent: pdfApiPayload?.saveCopyConsent === "true",
        isDraft: pdfApiPayload?.hasDraftEnabled === "true",
        languageCode: language,
        records: [record],
        pdfFiles: VECON_PDF_FILES.filter(
          (f) =>
            f.fileName.includes(`_${language}.`) &&
            !(
              f.fileName.startsWith("VECON_NoticeForm") && !isMarriedOrConjugal
            ) &&
            isConsentFormIncluded(f.fileName) &&
            !(
              f.fileName.startsWith("VECON_SingleName") &&
              data?.isNameChangeForCulturalReason !== "true"
            ),
        ),
      };

      setIsBusy(true);

      if (pdfApiPayload?.saveCopyConsent === "true") {
        await DataUtils.saveDraft(programContainer);
      }

      axios
        .post(url, payload, {
          headers: {
            Authorization: `Bearer ${SessionStorageService.getPSToken()}`,
            "x-recaptcha-token": recaptchaToken,
          },
        })
        .then((res) => {
          if (res?.status !== 200 || res?.data?.errorCode?.length > 0) {
            updateErrorResponse(res?.data, shouldOverrideMessage(res));
            setSummaryVisible(false);
            setSubmissionError(true);
            setIsBusy(false);
            return;
          }

          setConfirmationVisible(true);
          setSummaryVisible(false);
          setTransactionId(res?.data?.data?.transactionId || "");
          const responseData = {
            ...(res?.data?.data || {}),
            saveCopyConsent: pdfApiPayload?.saveCopyConsent === "true",
          };
          setRenewalResponse(responseData);
          DataUtils.clearDraft(
            programContainer.CategoryId,
            programContainer.Profile?.email,
            programContainer.draftId,
          );
          programContainer.draftId = undefined;
          cleanupAfterSubmit();

          if (
            programContainer.Profile?.userId &&
            programContainer.Profile?.token
          ) {
            getCxpMessages(programContainer.Profile?.token, {
              pageNumber: 1,
              pageSize: 20,
              search: "",
            })
              .then((messages) => {
                console.log(
                  "Refresh Cxp inbox messages after submit:",
                  messages,
                );
                if (messages?.messages) {
                  const unread1 =
                    messages?.messages?.filter((msg: any) => !msg?.isRead)
                      .length ?? 0;
                  const unread2 =
                    programContainer.Profile?.messages?.filter(
                      (msg: any) => !msg?.isRead,
                    ).length ?? 0;
                  if (unread1 !== unread2) {
                    const updatedContainer = {
                      ...programContainer,
                      Profile: {
                        ...programContainer.Profile,
                        messages: messages?.messages,
                      },
                    };
                    callback(updatedContainer);
                  }
                }
              })
              .catch((ex) => {
                console.log("Error refreshing Cxp Messages", ex);
              })
              .finally(() => {
                setIsBusy(false);
              });
          } else {
            setIsBusy(false);
          }
        })
        .catch((err) => {
          setIsBusy(false);
          updateErrorResponse(
            err.response?.data,
            shouldOverrideMessage(err?.response),
          );
          setSummaryVisible(false);
          setSubmissionError(true);
          console.log(err);
        })
        .finally(() => {
          setIsBusy(false);
        });
    }
  };

  const props = useMemo(
    () => ({
      isBusy,
      confirmationVisible,
      summaryVisible,
      transactionId,
      submissionError,
      validationErrMsg,
      renewalResponse,
      captchaToken,
    }),
    [
      isBusy,
      confirmationVisible,
      summaryVisible,
      transactionId,
      submissionError,
      validationErrMsg,
      renewalResponse,
      captchaToken,
    ],
  );

  useEffect(() => {
    onButtonClick(props);
  }, [props]);

  const processStepperScript = (currentSection: FormSections) => {
    let destinationIndex = null;
    if (currentSection?.CancelButtonScript) {
      const customJSFunc = eval(`(${currentSection.CancelButtonScript})`);
      destinationIndex = customJSFunc(currentSection);
    }
    return destinationIndex;
  };

  return (
    <div className="ontario-margin-top-24-!">
      <ReCaptchaV3 lang={isEng ? "en" : "fr"}>
        <ReCaptchaButton
          key={validationErrMsg?.toString()}
          title={isEng ? "Submit" : "Soumettre"}
          action="submitVECON"
          buttonHandler={handleSubmit}
        >
          {isEng ? "Submit" : "Soumettre"}
        </ReCaptchaButton>
      </ReCaptchaV3>
      {section?.IncludingCancelButton === "Y" && (
        <button
          className="ontario-button ontario-button--secondary"
          onClick={() => {
            if (section?.CancelButtonScript) {
              const stepperIndex = processStepperScript(section);
              callbackStepIndex(stepperIndex);
            }
          }}
        >
          {isEng ? "Cancel" : "Annuler"}
        </button>
      )}
    </div>
  );
};

export default VECONHandleSubmitComponent;
